import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Activity, ChefHat, MessageSquare, 
  TrendingUp, Monitor, Tablet, Smartphone,
  ArrowLeft, RefreshCw, Search, ChevronDown, ChevronUp, X,
  Settings
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const API_URL = window.location.origin;

// App version - update this when deploying to verify frontend is updated
const APP_VERSION = '2025.02.22.1';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('last_active');
  const [sortDir, setSortDir] = useState('desc');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPersona, setSelectedPersona] = useState(null); // For filtering by persona
  
  // App Settings
  const [appSettings, setAppSettings] = useState({ public_hives: 'disabled' });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Landing Analytics
  const [landingAnalytics, setLandingAnalytics] = useState(null);
  const [landingLoading, setLandingLoading] = useState(false);

  useEffect(() => {
    loadDashboard();
    loadUsers();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAppSettings(data.settings || { public_hives: 'disabled' });
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  };

  const saveSettings = async (newSettings) => {
    setSavingSettings(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSettings)
      });
      if (response.ok) {
        const data = await response.json();
        setAppSettings(data.settings);
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setSavingSettings(false);
    }
  };

  const loadLandingAnalytics = async (days = 30) => {
    setLandingLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/landing-analytics?days=${days}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setLandingAnalytics(data);
      }
    } catch (err) {
      console.error('Failed to load landing analytics:', err);
    } finally {
      setLandingLoading(false);
    }
  };

  const loadDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard`, {
        credentials: 'include'
      });
      if (response.status === 403) {
        setError('Admin access required');
        return;
      }
      if (!response.ok) throw new Error('Failed to load dashboard');
      const data = await response.json();
      setDashboard(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to load users');
      const data = await response.json();
      setUsers(data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUserDetail = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to load user detail');
      const data = await response.json();
      setUserDetail(data);
      setSelectedUser(userId);
    } catch (err) {
      console.error('Failed to load user detail:', err);
    }
  };

  const refresh = () => {
    setLoading(true);
    setSelectedPersona(null);
    loadDashboard();
    loadUsers();
  };

  // Calculate persona counts
  const personaCounts = useMemo(() => {
    const counts = {};
    users.forEach(u => {
      const persona = u.primary_persona || '👀 Passive';
      counts[persona] = (counts[persona] || 0) + 1;
    });
    return counts;
  }, [users]);

  // Calculate aggregate stats for selected persona
  const personaStats = useMemo(() => {
    if (!selectedPersona) return null;
    
    const personaUsers = users.filter(u => u.primary_persona === selectedPersona);
    
    return {
      count: personaUsers.length,
      totalRecipes: personaUsers.reduce((sum, u) => sum + (u.total_recipes || 0), 0),
      recipesFromChat: personaUsers.reduce((sum, u) => sum + (u.recipes_from_chat || 0), 0),
      recipesFromUrl: personaUsers.reduce((sum, u) => sum + (u.recipes_from_url || 0), 0),
      cookingDates: personaUsers.reduce((sum, u) => sum + (u.currently_cooking_dates || 0), 0),
      shoppingListDates: personaUsers.reduce((sum, u) => sum + (u.shopping_list_dates || 0), 0),
      miseEnPlaceDates: personaUsers.reduce((sum, u) => sum + (u.mise_en_place_dates || 0), 0),
      chatSessions: personaUsers.reduce((sum, u) => sum + (u.chat_sessions || 0), 0),
      chatMessages: personaUsers.reduce((sum, u) => sum + (u.chat_messages_sent || 0), 0),
      hivesJoined: personaUsers.reduce((sum, u) => sum + (u.hives_joined || 0), 0),
      hivesCreated: personaUsers.reduce((sum, u) => sum + (u.hives_created || 0), 0),
      buzzPosts: personaUsers.reduce((sum, u) => sum + (u.buzz_posts || 0), 0),
      desktopEvents: personaUsers.reduce((sum, u) => sum + (u.desktop_events || 0), 0),
      tabletEvents: personaUsers.reduce((sum, u) => sum + (u.tablet_events || 0), 0),
      mobileEvents: personaUsers.reduce((sum, u) => sum + (u.mobile_events || 0), 0),
    };
  }, [users, selectedPersona]);

  // Filter and sort users
  const filteredUsers = users
    .filter(u => {
      const matchesSearch = u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesPersona = !selectedPersona || u.primary_persona === selectedPersona;
      return matchesSearch && matchesPersona;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle dates
      if (sortField === 'last_active' || sortField === 'created_at') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      }
      
      if (sortDir === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff/86400000)}d ago`;
    
    return d.toLocaleDateString();
  };

  if (loading) {
    return <LoadingSpinner message="Loading admin dashboard..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/recipe-box')}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/recipe-box')}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Admin Dashboard</h1>
                <p className="text-xs text-gray-400">v{APP_VERSION}</p>
              </div>
            </div>
            <button
              onClick={refresh}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw size={18} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => { setActiveTab('overview'); setSelectedPersona(null); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'overview' 
                ? 'bg-amber-600 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => { setActiveTab('personas'); setSelectedPersona(null); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'personas' 
                ? 'bg-amber-600 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Personas
          </button>
          <button
            onClick={() => { setActiveTab('users'); setSelectedPersona(null); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'users' 
                ? 'bg-amber-600 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Users ({users.length})
          </button>
          <button
            onClick={() => { setActiveTab('settings'); setSelectedPersona(null); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'settings' 
                ? 'bg-amber-600 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Settings size={16} />
            Settings
          </button>
          <button
            onClick={() => { setActiveTab('landing'); setSelectedPersona(null); if (!landingAnalytics) loadLandingAnalytics(); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'landing' 
                ? 'bg-amber-600 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <TrendingUp size={16} />
            Landing Analytics
          </button>
        </div>

        {activeTab === 'overview' && dashboard && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{dashboard.users.total}</p>
                    <p className="text-sm text-gray-500">Total Users</p>
                  </div>
                </div>
                <p className="text-xs text-green-600 mt-2">+{dashboard.users.new_this_week} this week</p>
              </div>
              
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Activity size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{dashboard.users.active_this_week}</p>
                    <p className="text-sm text-gray-500">Active (7d)</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <ChefHat size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{dashboard.content.total_recipes}</p>
                    <p className="text-sm text-gray-500">Recipes</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <MessageSquare size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{dashboard.content.total_buzz_posts}</p>
                    <p className="text-sm text-gray-500">Buzz Posts</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Device & Events */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Device Breakdown */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Monitor size={18} />
                  Device Usage
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Monitor size={16} className="text-gray-500" />
                      <span className="text-sm text-gray-600">Desktop</span>
                    </div>
                    <span className="font-medium">{dashboard.devices.desktop || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Tablet size={16} className="text-gray-500" />
                      <span className="text-sm text-gray-600">Tablet</span>
                    </div>
                    <span className="font-medium">{dashboard.devices.tablet || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Smartphone size={16} className="text-gray-500" />
                      <span className="text-sm text-gray-600">Mobile</span>
                    </div>
                    <span className="font-medium">{dashboard.devices.mobile || 0}</span>
                  </div>
                </div>
              </div>

              {/* Top Events */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <TrendingUp size={18} />
                  Top Events
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {dashboard.top_events.slice(0, 10).map((event, idx) => (
                    <div key={event._id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 truncate">{event._id}</span>
                      <span className="font-medium text-slate-800">{event.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Events by Category */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-slate-800 mb-4">Events by Category</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">
                    {dashboard.events_by_category.recipe_management || 0}
                  </p>
                  <p className="text-sm text-gray-600">Recipe Management</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">
                    {dashboard.events_by_category.cooking_workflow || 0}
                  </p>
                  <p className="text-sm text-gray-600">Cooking Workflow</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">
                    {dashboard.events_by_category.ai_features || 0}
                  </p>
                  <p className="text-sm text-gray-600">AI Features</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">
                    {dashboard.events_by_category.social || 0}
                  </p>
                  <p className="text-sm text-gray-600">Social</p>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'personas' && (
          <>
            {/* Persona Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              {['📚 Collector', '👨‍🍳 Home Cook', '🤖 AI Explorer', '🐝 Social Bee', '⭐ Power User', '👀 Passive'].map(persona => (
                <button
                  key={persona}
                  onClick={() => setSelectedPersona(selectedPersona === persona ? null : persona)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    selectedPersona === persona 
                      ? 'border-amber-600 bg-amber-50' 
                      : 'border-gray-200 bg-white hover:border-amber-300'
                  }`}
                >
                  <div className="text-2xl mb-2">{persona.split(' ')[0]}</div>
                  <div className="text-sm font-medium text-slate-700">{persona.split(' ').slice(1).join(' ')}</div>
                  <div className="text-2xl font-bold text-slate-800 mt-1">{personaCounts[persona] || 0}</div>
                </button>
              ))}
            </div>

            {/* Persona Detail Stats */}
            {selectedPersona && personaStats && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800 text-lg">
                    {selectedPersona} - Aggregate Stats ({personaStats.count} users)
                  </h3>
                  <button 
                    onClick={() => setSelectedPersona(null)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <X size={18} />
                  </button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Recipe Management */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-2">📚 Recipes</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total</span>
                        <span className="font-medium">{personaStats.totalRecipes}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">From Chat</span>
                        <span className="font-medium">{personaStats.recipesFromChat}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">From URL</span>
                        <span className="font-medium">{personaStats.recipesFromUrl}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Cooking Workflow */}
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-800 mb-2">👨‍🍳 Cooking</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Cook Days</span>
                        <span className="font-medium">{personaStats.cookingDates}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Shopping Lists</span>
                        <span className="font-medium">{personaStats.shoppingListDates}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Mise en Place</span>
                        <span className="font-medium">{personaStats.miseEnPlaceDates}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* AI Features */}
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h4 className="font-medium text-purple-800 mb-2">🤖 AI Usage</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Chat Sessions</span>
                        <span className="font-medium">{personaStats.chatSessions}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Messages Sent</span>
                        <span className="font-medium">{personaStats.chatMessages}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Social */}
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <h4 className="font-medium text-amber-800 mb-2">🐝 Social</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Hives Joined</span>
                        <span className="font-medium">{personaStats.hivesJoined}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Hives Created</span>
                        <span className="font-medium">{personaStats.hivesCreated}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Buzz Posts</span>
                        <span className="font-medium">{personaStats.buzzPosts}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Device breakdown for this persona */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h4 className="font-medium text-gray-700 mb-2">Device Usage</h4>
                  <div className="flex gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <Monitor size={16} className="text-gray-500" />
                      <span>Desktop: <strong>{personaStats.desktopEvents}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Tablet size={16} className="text-gray-500" />
                      <span>Tablet: <strong>{personaStats.tabletEvents}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Smartphone size={16} className="text-gray-500" />
                      <span>Mobile: <strong>{personaStats.mobileEvents}</strong></span>
                    </div>
                  </div>
                </div>
                
                {/* Link to view users in this persona */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setActiveTab('users')}
                    className="text-amber-600 hover:text-amber-700 font-medium text-sm"
                  >
                    View {personaStats.count} users in this persona →
                  </button>
                </div>
              </div>
            )}

            {!selectedPersona && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center text-gray-500">
                Click on a persona card above to see aggregate statistics
              </div>
            )}
          </>
        )}

        {activeTab === 'users' && (
          <>
            {/* Search and Filter */}
            <div className="mb-4 flex gap-4">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              {selectedPersona && (
                <button
                  onClick={() => setSelectedPersona(null)}
                  className="flex items-center gap-2 px-3 py-2 bg-amber-100 text-amber-700 rounded-lg text-sm"
                >
                  {selectedPersona}
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">User</th>
                      <th 
                        className="text-center px-3 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                        onClick={() => toggleSort('total_recipes')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Recipes <SortIcon field="total_recipes" />
                        </div>
                      </th>
                      <th 
                        className="text-center px-3 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                        onClick={() => toggleSort('currently_cooking_dates')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Cook Days <SortIcon field="currently_cooking_dates" />
                        </div>
                      </th>
                      <th 
                        className="text-center px-3 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                        onClick={() => toggleSort('chat_sessions')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Chats <SortIcon field="chat_sessions" />
                        </div>
                      </th>
                      <th 
                        className="text-center px-3 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                        onClick={() => toggleSort('hives_joined')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Hives <SortIcon field="hives_joined" />
                        </div>
                      </th>
                      <th 
                        className="text-center px-3 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                        onClick={() => toggleSort('last_active')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Last Active <SortIcon field="last_active" />
                        </div>
                      </th>
                      <th 
                        className="text-center px-3 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                        onClick={() => toggleSort('ai_total_cost')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          AI Cost <SortIcon field="ai_total_cost" />
                        </div>
                      </th>
                      <th className="text-center px-3 py-3 font-medium text-gray-600">Persona</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredUsers.map((user) => (
                      <tr 
                        key={user.user_id}
                        onClick={() => loadUserDetail(user.user_id)}
                        className="hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {user.picture ? (
                              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-500 text-xs">
                                  {user.name?.charAt(0) || '?'}
                                </span>
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-slate-800">{user.name}</p>
                              <p className="text-xs text-gray-500">{user.email}</p>
                            </div>
                            {user.is_admin && (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                                Admin
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="text-center px-3 py-3">
                          <span className="font-medium">{user.total_recipes}</span>
                          {user.recipes_from_chat > 0 && (
                            <span className="text-xs text-purple-600 ml-1">
                              ({user.recipes_from_chat} AI)
                            </span>
                          )}
                        </td>
                        <td className="text-center px-3 py-3">
                          <span className={`font-medium ${user.currently_cooking_dates > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                            {user.currently_cooking_dates}
                          </span>
                        </td>
                        <td className="text-center px-3 py-3">
                          <span className={`font-medium ${user.chat_sessions > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                            {user.chat_sessions}
                          </span>
                        </td>
                        <td className="text-center px-3 py-3">
                          <span className="font-medium">{user.hives_joined}</span>
                          {user.hives_created > 0 && (
                            <span className="text-xs text-amber-600 ml-1">
                              (+{user.hives_created})
                            </span>
                          )}
                        </td>
                        <td className="text-center px-3 py-3 text-gray-500">
                          {formatDate(user.last_active)}
                        </td>
                        <td className="text-center px-3 py-3">
                          <span className={`font-medium ${user.ai_total_cost > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
                            ${(user.ai_total_cost || 0).toFixed(2)}
                          </span>
                          {user.ai_total_calls > 0 && (
                            <span className="text-xs text-gray-400 ml-1">
                              ({user.ai_total_calls})
                            </span>
                          )}
                        </td>
                        <td className="text-center px-3 py-3">
                          <span className="text-sm">{user.primary_persona}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-6">App Settings</h2>
            
            {/* Public Hives Feature Flag */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-2">Public Hives</h3>
              <p className="text-sm text-gray-500 mb-4">
                Control whether users can create and discover public hives. When disabled, only private hives are available.
              </p>
              
              <div className="space-y-2">
                {[
                  { value: 'disabled', label: 'Disabled', desc: 'Only private hives. No search or discovery.' },
                  { value: 'admin_only', label: 'Admin Only', desc: 'Admins can create public hives and see discovery features.' },
                  { value: 'enabled', label: 'Enabled for All', desc: 'All users can create public hives and use discovery.' }
                ].map(option => (
                  <label 
                    key={option.value}
                    className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      appSettings.public_hives === option.value 
                        ? 'border-amber-600 bg-amber-50' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="public_hives"
                      value={option.value}
                      checked={appSettings.public_hives === option.value}
                      onChange={(e) => saveSettings({ public_hives: e.target.value })}
                      className="mt-1 text-amber-600 focus:ring-amber-500"
                      disabled={savingSettings}
                    />
                    <div>
                      <div className="font-medium text-gray-900">{option.label}</div>
                      <div className="text-sm text-gray-500">{option.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
              
              {savingSettings && (
                <p className="text-sm text-amber-600 mt-2">Saving...</p>
              )}
            </div>
            
            {/* AI Chat Limit */}
            <div className="mb-6 border-t border-gray-200 pt-6">
              <h3 className="font-semibold text-gray-800 mb-2">🤖 AI Chat Limit</h3>
              <p className="text-sm text-gray-500 mb-4">
                Monthly limit on AI chat messages per user. Admins have unlimited. Founding members get 2x limit.
              </p>
              
              <div className="flex items-center gap-4">
                <input
                  type="number"
                  value={appSettings.ai_chat_limit_monthly || 100}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    saveSettings({ ai_chat_limit_monthly: value });
                  }}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-center"
                  min="0"
                  step="10"
                  disabled={savingSettings}
                />
                <span className="text-sm text-gray-600">messages per month</span>
                <span className="text-xs text-gray-400">(0 = unlimited)</span>
              </div>
              
              <div className="mt-3 flex gap-2">
                {[50, 100, 200, 500, 0].map(preset => (
                  <button
                    key={preset}
                    onClick={() => saveSettings({ ai_chat_limit_monthly: preset })}
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${
                      (appSettings.ai_chat_limit_monthly || 100) === preset
                        ? 'bg-amber-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    disabled={savingSettings}
                  >
                    {preset === 0 ? '∞' : preset}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Founding Members Section */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-semibold text-gray-800 mb-2">🐝 Founding Members</h3>
              <p className="text-sm text-gray-500 mb-4">
                Early adopters with free access. Currently {users.filter(u => u.is_founding_member).length} founding member(s).
              </p>
              
              {/* List of founding members */}
              {users.filter(u => u.is_founding_member).length > 0 && (
                <div className="mb-4 space-y-2">
                  {users.filter(u => u.is_founding_member).map(user => (
                    <div key={user.user_id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center text-amber-700 font-medium">
                            {user.name?.charAt(0) || '?'}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{user.name}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-amber-700">
                          Free until {user.free_until ? new Date(user.free_until).toLocaleDateString() : '2026'}
                        </span>
                        <button
                          onClick={async () => {
                            if (window.confirm(`Revoke founding member status from ${user.name}?`)) {
                              await fetch(`${API_URL}/api/admin/founding-member/${user.user_id}`, {
                                method: 'DELETE',
                                credentials: 'include'
                              });
                              loadUsers();
                            }
                          }}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Quick add from recent users */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Grant founding member status:</p>
                <div className="flex flex-wrap gap-2">
                  {users.filter(u => !u.is_founding_member).slice(0, 10).map(user => (
                    <button
                      key={user.user_id}
                      onClick={async () => {
                        await fetch(`${API_URL}/api/admin/founding-member/${user.user_id}`, {
                          method: 'POST',
                          credentials: 'include',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ free_until: '2026-01-01' })
                        });
                        loadUsers();
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-amber-100 rounded-full text-sm transition-colors"
                      title={user.email}
                    >
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
                      ) : (
                        <span className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-xs">
                          {user.name?.charAt(0) || '?'}
                        </span>
                      )}
                      <span>{user.name?.split(' ')[0]}</span>
                      <span className="text-amber-600">+</span>
                    </button>
                  ))}
                </div>
                {users.filter(u => !u.is_founding_member).length > 10 && (
                  <p className="text-xs text-gray-400 mt-2">
                    Showing first 10 users. Use the Users tab to find others.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Landing Analytics Tab */}
        {activeTab === 'landing' && (
          <div className="space-y-6">
            {/* Header with Refresh */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800">Landing Page Analytics</h2>
              <div className="flex items-center gap-3">
                <select 
                  onChange={(e) => loadLandingAnalytics(parseInt(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  defaultValue="30"
                >
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                </select>
                <button 
                  onClick={() => loadLandingAnalytics()}
                  className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={landingLoading}
                >
                  <RefreshCw size={18} className={landingLoading ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>

            {landingLoading && (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            )}

            {!landingLoading && landingAnalytics && (
              <>
                {/* Top Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <p className="text-sm text-gray-500">Total Visitors</p>
                    <p className="text-2xl font-bold text-slate-800">{landingAnalytics.total_visitors}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <p className="text-sm text-gray-500">Converted</p>
                    <p className="text-2xl font-bold text-green-600">{landingAnalytics.converted_visitors}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <p className="text-sm text-gray-500">Conversion Rate</p>
                    <p className="text-2xl font-bold text-amber-600">{landingAnalytics.conversion_rate}%</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <p className="text-sm text-gray-500">Avg Time on Page</p>
                    <p className="text-2xl font-bold text-slate-800">{Math.round(landingAnalytics.avg_time_on_page_seconds / 60)}m {landingAnalytics.avg_time_on_page_seconds % 60}s</p>
                  </div>
                </div>

                {/* Section Interest Comparison */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Non-Converted Interest */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <span className="text-red-500">📉</span> 
                      Non-Converted Visitors Viewed
                    </h3>
                    <p className="text-xs text-gray-500 mb-4">What sections do visitors view before leaving WITHOUT signing up?</p>
                    <div className="space-y-3">
                      {Object.entries(landingAnalytics.non_converted_section_interest || {})
                        .sort((a, b) => b[1] - a[1])
                        .map(([section, count]) => (
                          <div key={section} className="flex items-center justify-between">
                            <span className="text-sm text-gray-700 capitalize">{section.replace(/_/g, ' ')}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-red-400 rounded-full"
                                  style={{ width: `${Math.min(100, (count / Math.max(...Object.values(landingAnalytics.non_converted_section_interest || {1: 1}))) * 100)}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium text-gray-600 w-8 text-right">{count}</span>
                            </div>
                          </div>
                        ))}
                      {Object.keys(landingAnalytics.non_converted_section_interest || {}).length === 0 && (
                        <p className="text-gray-500 text-sm">No data yet</p>
                      )}
                    </div>
                  </div>

                  {/* Converted Interest */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <span className="text-green-500">📈</span>
                      Converted Visitors Viewed
                    </h3>
                    <p className="text-xs text-gray-500 mb-4">What sections do visitors view before signing up?</p>
                    <div className="space-y-3">
                      {Object.entries(landingAnalytics.converted_section_interest || {})
                        .sort((a, b) => b[1] - a[1])
                        .map(([section, count]) => (
                          <div key={section} className="flex items-center justify-between">
                            <span className="text-sm text-gray-700 capitalize">{section.replace(/_/g, ' ')}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-green-500 rounded-full"
                                  style={{ width: `${Math.min(100, (count / Math.max(...Object.values(landingAnalytics.converted_section_interest || {1: 1}))) * 100)}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium text-gray-600 w-8 text-right">{count}</span>
                            </div>
                          </div>
                        ))}
                      {Object.keys(landingAnalytics.converted_section_interest || {}).length === 0 && (
                        <p className="text-gray-500 text-sm">No data yet</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section Views & Time */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-slate-800 mb-4">Section Engagement (All Visitors)</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left py-2 text-gray-500 font-medium">Section</th>
                          <th className="text-right py-2 text-gray-500 font-medium">Views</th>
                          <th className="text-right py-2 text-gray-500 font-medium">Avg Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(landingAnalytics.section_views || {})
                          .sort((a, b) => b[1] - a[1])
                          .map(([section, views]) => (
                            <tr key={section} className="border-b border-gray-50">
                              <td className="py-3 text-gray-700 capitalize">{section.replace(/_/g, ' ')}</td>
                              <td className="py-3 text-right font-medium">{views}</td>
                              <td className="py-3 text-right text-gray-500">
                                {landingAnalytics.section_avg_time_seconds?.[section] 
                                  ? `${landingAnalytics.section_avg_time_seconds[section]}s` 
                                  : '-'}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* CTA Clicks */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-slate-800 mb-4">CTA Button Clicks</h3>
                  <div className="flex flex-wrap gap-4">
                    {Object.entries(landingAnalytics.cta_clicks || {}).map(([cta, clicks]) => (
                      <div key={cta} className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs text-amber-700 capitalize">{cta.replace(/_/g, ' ')}</p>
                        <p className="text-xl font-bold text-amber-600">{clicks}</p>
                      </div>
                    ))}
                    {Object.keys(landingAnalytics.cta_clicks || {}).length === 0 && (
                      <p className="text-gray-500 text-sm">No CTA clicks tracked yet</p>
                    )}
                  </div>
                </div>

                {/* Link to Landing Page */}
                <div className="text-center py-4">
                  <a 
                    href="/landing.html" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-amber-600 hover:text-amber-700 underline"
                  >
                    View Landing Page →
                  </a>
                </div>
              </>
            )}

            {!landingLoading && !landingAnalytics && (
              <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
                <p className="text-gray-500 mb-4">No analytics data yet</p>
                <button 
                  onClick={() => loadLandingAnalytics()}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                >
                  Load Analytics
                </button>
              </div>
            )}
          </div>
        )}

        {/* User Detail Modal */}
        {selectedUser && userDetail && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {userDetail.user.picture ? (
                    <img src={userDetail.user.picture} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-200" />
                  )}
                  <div>
                    <h3 className="font-bold text-slate-800">{userDetail.user.name}</h3>
                    <p className="text-sm text-gray-500">{userDetail.user.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedUser(null);
                    setUserDetail(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  ✕
                </button>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[70vh]">
                {/* User Stats */}
                <div className="grid grid-cols-4 gap-3 mb-6">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xl font-bold text-slate-800">{userDetail.recipes.length}</p>
                    <p className="text-xs text-gray-500">Recipes</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xl font-bold text-slate-800">{userDetail.hives.length}</p>
                    <p className="text-xs text-gray-500">Hives</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xl font-bold text-slate-800">{userDetail.buzz_posts.length}</p>
                    <p className="text-xs text-gray-500">Buzz</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xl font-bold text-slate-800">{userDetail.events.length}</p>
                    <p className="text-xs text-gray-500">Events</p>
                  </div>
                </div>

                {/* Recent Activity */}
                <h4 className="font-semibold text-slate-800 mb-3">Recent Activity</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {userDetail.events.slice(0, 50).map((event, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm py-2 border-b border-gray-50">
                      <div>
                        <span className="font-medium text-slate-700">{event.event_name}</span>
                        <span className="text-gray-400 ml-2">({event.device_type})</span>
                      </div>
                      <span className="text-gray-500 text-xs">
                        {formatDate(event.created_at)}
                      </span>
                    </div>
                  ))}
                  {userDetail.events.length === 0 && (
                    <p className="text-gray-500 text-sm">No events tracked yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
